import { call, put, select } from 'redux-saga/effects';
import * as R from 'remeda';
import { improvementAreaSelectors } from 'store/data/improvementArea';
import { suggestionActions, suggestionSelectors } from 'store/data/suggestion';
import { createAction } from 'store/glue/operation/createAction';
import { initializeSuggestions } from 'store/glue/operation/initializeSuggestions';
import { sagaWrapper } from 'store/helpers';
import { ActionType } from 'typesafe-actions';
import { timeUtility } from 'utility';

import * as improvementsActions from './actions';

// Creates a new action pre-populated from an existing suggestion, then
// refreshes the suggestion to reflect its new actionIds linkage.
export function* startCreateActionFromSuggestionSaga(
  action: ActionType<
    typeof improvementsActions.startCreateActionFromSuggestion
  >,
) {
  function* saga() {
    const { suggestionId } = action.payload;

    const suggestion: ReturnType<typeof suggestionSelectors.item> =
      yield select(suggestionSelectors.item, {
        id: suggestionId,
      });
    if (!R.isDefined(suggestion)) return;

    const today = timeUtility.getToday();
    const tomorrow = timeUtility.step({
      step: 'next',
      stepSize: 'week',
      timeDate: today,
    });

    // Inherit improvementAreaId from the suggestion; the new API's createAction
    // guard early-returns when it's null, so legacy suggestions without an
    // area fall back to the default (lowest-sortOrder) area — same fallback
    // the mock-create sagas use.
    const defaultImprovementAreaId: ReturnType<
      typeof improvementAreaSelectors.defaultImprovementAreaId
    > = yield select(improvementAreaSelectors.defaultImprovementAreaId);

    const improvementAreaId =
      suggestion.improvementAreaId ?? defaultImprovementAreaId;

    const _action: Data.Action = {
      id: R.randomString(10),
      improvementAreaId,
      name: suggestion.name,
      suggestionIds: [suggestion.id],
      problemStatement: suggestion.problemStatement,
      solutionProposal: suggestion.solutionProposal,
      responsibleEmployee: null,
      definitionOfDone: '-',
      endAt: tomorrow,
      priority: 'None',
      startAt: today,
      status: 'New',
    };

    const createdAction: Data.Action | undefined = yield call(createAction, {
      action: _action,
    });
    if (!R.isDefined(createdAction)) return;

    yield put(improvementsActions.setSelectedActionId(createdAction.id));

    // Switch to the actions tab so the new action is visible
    yield put(improvementsActions.setActiveTabId('actions'));

    // Re-fetch all suggestions so actionIds linkage is up to date
    yield call(initializeSuggestions);

    // Optimistically mark the source suggestion ACCEPTED. The backend does
    // not auto-transition the suggestion's status when an action is created
    // linked to it, so without this the UI would leave it stuck at NEW even
    // though it has been actioned. Applied AFTER the refetch so it survives
    // — `setDomain` would otherwise clobber an earlier update.
    yield put(
      suggestionActions.update({ id: suggestion.id, status: 'ACCEPTED' }),
    );
  }

  yield call(sagaWrapper, { saga, type: action.type });
}
